import { forwardRef, useEffect, useRef, useState } from "react";
import moment from "moment-timezone";
import PropTypes from "prop-types";

import { Datepicker as BaseDatepicker } from "@/ui-lib/FormFields/Datepicker";
import { getCompanyDateFormat2Digit } from "@/utils/date";

const RangeDatepicker = forwardRef(
  (
    {
      className,
      onChange,
      placeholder,
      startDate,
      endDate,
      select,
      rangeStartLabel,
      rangeEndLabel,
      allowPartialRange = false,
      ...rest
    },
    ref
  ) => {
    const [start, setStart] = useState(() => {
      const initialStart = startDate ? moment(startDate).toDate() : null;
      return initialStart;
    });
    const [end, setEnd] = useState(() => {
      const initialEnd = endDate ? moment(endDate).toDate() : null;
      return initialEnd;
    });
    const isUpdatingFromPropsRef = useRef(false);

    useEffect(() => {
      isUpdatingFromPropsRef.current = true;

      const newStart = startDate ? moment(startDate).toDate() : null;
      const newEnd = endDate ? moment(endDate).toDate() : null;

      setStart(newStart);
      setEnd(newEnd);

      setTimeout(() => {
        isUpdatingFromPropsRef.current = false;
      }, 0);
    }, [startDate, endDate]);

    /**
     * Handles date range changes and calls the onChange callback
     * @param {Object} data - The date picker data object
     * @param {Array} data.value - Array containing start and end dates
     */
    const handleDateChange = (data) => {
      if (isUpdatingFromPropsRef.current) {
        return;
      }

      if (!data.type) {
        return;
      }

      const { value } = data;

      if (select !== "range") {
        return;
      }

      const startDateValue = value?.[0] ? moment(value[0]).toDate() : null;
      const endDateValue = value?.[1] ? moment(value[1]).toDate() : null;

      setStart(startDateValue);
      setEnd(endDateValue);

      if (!allowPartialRange && (!startDateValue || !endDateValue)) {
        return;
      }

      const formattedStartDate = startDateValue
        ? moment(startDateValue).format("YYYY-MM-DD")
        : null;
      const formattedEndDate = endDateValue ? moment(endDateValue).format("YYYY-MM-DD") : null;

      // Only call onChange if the values are actually different from current props
      const currentStartFormatted = startDate ? moment(startDate).format("YYYY-MM-DD") : null;
      const currentEndFormatted = endDate ? moment(endDate).format("YYYY-MM-DD") : null;

      if (
        formattedStartDate !== currentStartFormatted ||
        formattedEndDate !== currentEndFormatted
      ) {
        onChange({
          startDate: formattedStartDate,
          endDate: formattedEndDate,
        });
      }
    };

    return (
      <BaseDatepicker
        className={className}
        value={[start, end]}
        dateFormat={getCompanyDateFormat2Digit()}
        themeVariant="light"
        controls={["calendar"]}
        theme="ios"
        select={select}
        rangeStartLabel={rangeStartLabel}
        rangeEndLabel={rangeEndLabel}
        onChange={handleDateChange}
        placeholder={placeholder}
        ref={ref}
        {...rest}
      />
    );
  }
);

RangeDatepicker.defaultProps = {
  className: "",
  placeholder: "",
  select: "date",
  rangeStartLabel: "From",
  rangeEndLabel: "To",
  startDate: "",
  endDate: "",
  allowPartialRange: false,
};

RangeDatepicker.propTypes = {
  className: PropTypes.string,
  placeholder: PropTypes.string,
  select: PropTypes.oneOf(["date", "range"]),
  rangeStartLabel: PropTypes.string,
  rangeEndLabel: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  allowPartialRange: PropTypes.bool,
};

export default RangeDatepicker;
